// SEO utilities: slug generation, Bundesland maps, hreflang helpers

const UMLAUT_MAP: Record<string, string> = {
  ä: "ae", ö: "oe", ü: "ue", ß: "ss",
  Ä: "Ae", Ö: "Oe", Ü: "Ue",
};

export function slugify(text: string): string {
  let result = text.toLowerCase();
  for (const [char, replacement] of Object.entries(UMLAUT_MAP)) {
    result = result.replaceAll(char.toLowerCase(), replacement);
  }
  return result
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function occupationSlug(occupationName: string): string {
  return slugify(occupationName);
}

/** Reverse slug for display (best-effort — capitalize words, no umlaut restore) */
export function deslugify(slug: string): string {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// All 16 German Bundesländer: slug → regionCode
export const BUNDESLAND_SLUGS: Record<string, string> = {
  "baden-wuerttemberg": "BW",
  "bayern": "BY",
  "berlin": "BE",
  "brandenburg": "BB",
  "bremen": "HB",
  "hamburg": "HH",
  "hessen": "HE",
  "mecklenburg-vorpommern": "MV",
  "niedersachsen": "NI",
  "nordrhein-westfalen": "NW",
  "rheinland-pfalz": "RP",
  "saarland": "SL",
  "sachsen": "SN",
  "sachsen-anhalt": "ST",
  "schleswig-holstein": "SH",
  "thueringen": "TH",
};

// regionCode → { de, en } labels
export const BUNDESLAND_LABELS: Record<string, { de: string; en: string }> = {
  BW: { de: "Baden-Württemberg", en: "Baden-Württemberg" },
  BY: { de: "Bayern", en: "Bavaria" },
  BE: { de: "Berlin", en: "Berlin" },
  BB: { de: "Brandenburg", en: "Brandenburg" },
  HB: { de: "Bremen", en: "Bremen" },
  HH: { de: "Hamburg", en: "Hamburg" },
  HE: { de: "Hessen", en: "Hesse" },
  MV: { de: "Mecklenburg-Vorpommern", en: "Mecklenburg-Western Pomerania" },
  NI: { de: "Niedersachsen", en: "Lower Saxony" },
  NW: { de: "Nordrhein-Westfalen", en: "North Rhine-Westphalia" },
  RP: { de: "Rheinland-Pfalz", en: "Rhineland-Palatinate" },
  SL: { de: "Saarland", en: "Saarland" },
  SN: { de: "Sachsen", en: "Saxony" },
  ST: { de: "Sachsen-Anhalt", en: "Saxony-Anhalt" },
  SH: { de: "Schleswig-Holstein", en: "Schleswig-Holstein" },
  TH: { de: "Thüringen", en: "Thuringia" },
};

// Reverse lookup: regionCode → slug
export const REGION_CODE_TO_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(BUNDESLAND_SLUGS).map(([slug, code]) => [code, slug])
);

/** Generate hreflang link tags for a given path */
export function generateHreflangLinks(path: string): Array<{ rel: string; hreflang: string; href: string }> {
  const siteUrl = (typeof process !== "undefined" && process.env?.SITE_URL) || "https://alchema.eu";
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return [
    { rel: "alternate", hreflang: "de", href: `${siteUrl}/de${cleanPath}` },
    { rel: "alternate", hreflang: "en", href: `${siteUrl}${cleanPath}` },
    { rel: "alternate", hreflang: "x-default", href: `${siteUrl}${cleanPath}` },
  ];
}

/** Format EUR salary for display */
export function formatSalary(amount: number): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(amount);
}
